import React, { useState } from 'react';
import './Auth.css';

/**
 * RegisterPage
 * @param {fn} onRegister  - (formData) => Promise<boolean>
 * @param {fn} onGoLogin   - navigate back to login
 * @param {string} error   - error message from useAuth
 * @param {boolean} loading
 */
export default function RegisterPage({ onRegister, onGoLogin, error, loading }) {
  const [form, setForm] = useState({
    name: '', email: '', mobile: '', role: 'Organiser',
    password: '', confirmPassword: '',
  });
  const [showPass, setShowPass]       = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [step, setStep]               = useState(1); // 2-step form

  const handleChange = (e) => {
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
    setFieldErrors((fe) => ({ ...fe, [e.target.name]: '' }));
  };

  const validateStep1 = () => {
    const errs = {};
    if (!form.name.trim())   errs.name   = 'Full name is required';
    if (!form.email.trim())  errs.email  = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(form.email)) errs.email = 'Enter a valid email';
    if (!form.mobile.trim()) errs.mobile = 'Mobile number is required';
    else if (!/^[6-9]\d{9}$/.test(form.mobile)) errs.mobile = 'Enter valid 10-digit mobile';
    return errs;
  };

  const validateStep2 = () => {
    const errs = {};
    if (!form.password)      errs.password = 'Password is required';
    else if (form.password.length < 6) errs.password = 'Minimum 6 characters';
    if (!form.confirmPassword) errs.confirmPassword = 'Please confirm your password';
    else if (form.password !== form.confirmPassword) errs.confirmPassword = 'Passwords do not match';
    return errs;
  };

  const handleNext = (e) => {
    e.preventDefault();
    const errs = validateStep1();
    if (Object.keys(errs).length) { setFieldErrors(errs); return; }
    setStep(2);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validateStep2();
    if (Object.keys(errs).length) { setFieldErrors(errs); return; }
    await onRegister(form);
  };

  const strength = (() => {
    const p = form.password;
    if (!p) return 0;
    let s = 0;
    if (p.length >= 6)  s++;
    if (p.length >= 10) s++;
    if (/[A-Z]/.test(p)) s++;
    if (/[0-9]/.test(p)) s++;
    if (/[^A-Za-z0-9]/.test(p)) s++;
    return s;
  })();

  const strengthLabel = ['', 'Weak', 'Fair', 'Good', 'Strong', 'Very Strong'][strength];
  const strengthColor = ['', '#f87171', '#f59e0b', '#facc15', '#4ade80', '#22c55e'][strength];

  return (
    <div className="auth-shell">
      {/* Left branding panel */}
      <div className="auth-brand">
        <div className="auth-brand__inner">
          <div className="auth-brand__logo">
            <span className="auth-brand__logo-icon">🌽</span>
          </div>
          <h1 className="auth-brand__title">Join Maaze</h1>
          <p className="auth-brand__tagline">Create your account to start managing seed distribution</p>

          <div className="auth-brand__roles">
            {[
              { role: 'Owner',     icon: '👑', desc: 'Full system access, reports & financials' },
              { role: 'Organiser', icon: '🏢', desc: 'Manage villages, farmers & distribution'  },
              { role: 'Admin',     icon: '⚙️', desc: 'Accounts, ledger & operational data'     },
            ].map((r) => (
              <div
                key={r.role}
                className={`auth-role-card ${form.role === r.role ? 'auth-role-card--active' : ''}`}
                onClick={() => setForm((f) => ({ ...f, role: r.role }))}
              >
                <span className="auth-role-card__icon">{r.icon}</span>
                <div>
                  <div className="auth-role-card__name">{r.role}</div>
                  <div className="auth-role-card__desc">{r.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="auth-brand__blob auth-brand__blob--1" />
        <div className="auth-brand__blob auth-brand__blob--2" />
      </div>

      {/* Right form panel */}
      <div className="auth-form-panel">
        <div className="auth-form-card">

          {/* Header */}
          <div className="auth-form-card__header">
            <div className="auth-form-card__mobile-logo">🌽 Maaze</div>
            <h2 className="auth-form-card__title">Create Account</h2>
            <p className="auth-form-card__sub">
              {step === 1 ? 'Step 1 of 2 – Personal information' : 'Step 2 of 2 – Set your password'}
            </p>
          </div>

          {/* Step progress */}
          <div className="auth-steps">
            <div className={`auth-steps__step ${step >= 1 ? 'auth-steps__step--done' : ''}`}>
              <div className="auth-steps__circle">{step > 1 ? '✓' : '1'}</div>
              <span>Profile</span>
            </div>
            <div className={`auth-steps__connector ${step >= 2 ? 'auth-steps__connector--done' : ''}`} />
            <div className={`auth-steps__step ${step >= 2 ? 'auth-steps__step--done' : ''}`}>
              <div className="auth-steps__circle">2</div>
              <span>Security</span>
            </div>
          </div>

          {/* Global error */}
          {error && (
            <div className="auth-error-banner">
              <span>⚠️</span> {error}
            </div>
          )}

          {/* ── Step 1 ── */}
          {step === 1 && (
            <form className="auth-form" onSubmit={handleNext} noValidate>

              <div className="auth-field">
                <label className="auth-field__label">Full Name</label>
                <div className={`auth-field__wrap ${fieldErrors.name ? 'auth-field__wrap--error' : ''}`}>
                  <span className="auth-field__icon">👤</span>
                  <input className="auth-field__input" type="text" name="name" placeholder="Your full name"
                    value={form.name} onChange={handleChange} autoComplete="name" />
                </div>
                {fieldErrors.name && <span className="auth-field__error">{fieldErrors.name}</span>}
              </div>

              <div className="auth-field">
                <label className="auth-field__label">Email Address</label>
                <div className={`auth-field__wrap ${fieldErrors.email ? 'auth-field__wrap--error' : ''}`}>
                  <span className="auth-field__icon">✉️</span>
                  <input className="auth-field__input" type="email" name="email" placeholder="you@example.com"
                    value={form.email} onChange={handleChange} autoComplete="email" />
                </div>
                {fieldErrors.email && <span className="auth-field__error">{fieldErrors.email}</span>}
              </div>

              <div className="auth-field">
                <label className="auth-field__label">Mobile Number</label>
                <div className={`auth-field__wrap ${fieldErrors.mobile ? 'auth-field__wrap--error' : ''}`}>
                  <span className="auth-field__icon">📱</span>
                  <input className="auth-field__input" type="tel" name="mobile" placeholder="10-digit mobile"
                    value={form.mobile} onChange={handleChange} />
                </div>
                {fieldErrors.mobile && <span className="auth-field__error">{fieldErrors.mobile}</span>}
              </div>

              <div className="auth-field">
                <label className="auth-field__label">Role</label>
                <div className="auth-field__wrap">
                  <span className="auth-field__icon">🏷️</span>
                  <select className="auth-field__input auth-field__select" name="role"
                    value={form.role} onChange={handleChange}>
                    <option value="Owner">Owner</option>
                    <option value="Organiser">Organiser</option>
                    <option value="Admin">Admin</option>
                  </select>
                </div>
              </div>

              <button type="submit" className="auth-submit-btn">
                Continue →
              </button>
            </form>
          )}

          {/* ── Step 2 ── */}
          {step === 2 && (
            <form className="auth-form" onSubmit={handleSubmit} noValidate>

              <div className="auth-field">
                <label className="auth-field__label">Password</label>
                <div className={`auth-field__wrap ${fieldErrors.password ? 'auth-field__wrap--error' : ''}`}>
                  <span className="auth-field__icon">🔒</span>
                  <input className="auth-field__input" type={showPass ? 'text' : 'password'}
                    name="password" placeholder="Create a password"
                    value={form.password} onChange={handleChange} autoComplete="new-password" />
                  <button type="button" className="auth-field__toggle" onClick={() => setShowPass((s) => !s)}>
                    {showPass ? '🙈' : '👁'}
                  </button>
                </div>
                {fieldErrors.password && <span className="auth-field__error">{fieldErrors.password}</span>}

                {/* Strength meter */}
                {form.password && (
                  <div className="auth-strength">
                    <div className="auth-strength__bars">
                      {[1,2,3,4,5].map((n) => (
                        <div key={n} className="auth-strength__bar"
                          style={{ background: n <= strength ? strengthColor : undefined }} />
                      ))}
                    </div>
                    <span className="auth-strength__label" style={{ color: strengthColor }}>{strengthLabel}</span>
                  </div>
                )}
              </div>

              <div className="auth-field">
                <label className="auth-field__label">Confirm Password</label>
                <div className={`auth-field__wrap ${fieldErrors.confirmPassword ? 'auth-field__wrap--error' : ''}`}>
                  <span className="auth-field__icon">🔏</span>
                  <input className="auth-field__input" type={showConfirm ? 'text' : 'password'}
                    name="confirmPassword" placeholder="Re-enter password"
                    value={form.confirmPassword} onChange={handleChange} autoComplete="new-password" />
                  <button type="button" className="auth-field__toggle" onClick={() => setShowConfirm((s) => !s)}>
                    {showConfirm ? '🙈' : '👁'}
                  </button>
                </div>
                {fieldErrors.confirmPassword && <span className="auth-field__error">{fieldErrors.confirmPassword}</span>}
                {form.confirmPassword && form.password === form.confirmPassword && (
                  <span className="auth-field__match">✓ Passwords match</span>
                )}
              </div>

              {/* Terms */}
              <p className="auth-terms">
                By creating an account you agree to Maaze's <button type="button" className="auth-link">Terms of Service</button> and <button type="button" className="auth-link">Privacy Policy</button>.
              </p>

              <div style={{ display: 'flex', gap: 10 }}>
                <button type="button" className="auth-back-btn" onClick={() => setStep(1)}>← Back</button>
                <button type="submit" className="auth-submit-btn" disabled={loading} style={{ flex: 1 }}>
                  {loading ? <><span className="auth-spinner" /> Creating account…</> : 'Create Account →'}
                </button>
              </div>
            </form>
          )}

          {/* Footer */}
          <p className="auth-form-card__footer">
            Already have an account?{' '}
            <button type="button" className="auth-link auth-link--accent" onClick={onGoLogin}>
              Sign in
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
